<template>
  <Layout>
    <PageHeader :title="$t('authorizeAttendances.title')" :items="breadcrumbItems" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <p class="text-muted mb-3">{{ $t('authorizeAttendances.subtitle') }}</p>

            <div class="row mb-3">
              <div class="col-md-2">
                <label class="form-label">{{ $t('authorizeAttendances.startDate') }}</label>
                <input
                  id="authorize-start-date"
                  v-model="filters.start_date"
                  type="date"
                  class="form-control"
                />
              </div>
              <div class="col-md-2">
                <label class="form-label">{{ $t('authorizeAttendances.endDate') }}</label>
                <input
                  id="authorize-end-date"
                  v-model="filters.end_date"
                  type="date"
                  class="form-control"
                />
              </div>
              <div class="col-md-6">
                <label for="selectPatient" class="form-label">{{ $t('authorizeAttendances.filterByPatient') }}</label>
                <multiselect
                  id="selectPatient"
                  :key="'patient-' + multiselectKey"
                  v-model="filters.patient"
                  :options="listPatientsOptions"
                  :placeholder="$t('authorizeAttendances.filterByPatientPlaceholder')"
                  label="label"
                  track-by="id"
                  :multiple="false"
                  :allow-empty="true"
                  select-label="Pressione ENTER para selecionar"
                  deselect-label="Pressione ENTER para remover"
                  selected-label="Pressione ENTER para selecionar"
                >
                  <template #option="props">
                    {{ props.option.id }} - {{ props.option.name }}
                  </template>
                  <template #singleLabel="{ option }">
                    <span v-if="option">{{ option.id }} - {{ option.name }}</span>
                  </template>
                </multiselect>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-2">
                <label for="selectRequestType" class="form-label">{{ $t('authorizeAttendances.filterByRequestType') }}</label>
                <b-form-select
                  id="selectRequestType"
                  v-model="filters.request_type"
                  :options="requestTypeOptions"
                  class="form-control"
                />
              </div>
              <div class="col-md-4">
                <label for="selectSpecialty" class="form-label">{{ $t('authorizeAttendances.filterBySpecialty') }}</label>
                <multiselect
                  id="selectSpecialty"
                  :key="'specialty-' + multiselectKey"
                  v-model="filters.specialty"
                  :options="listSpecialtiesOptions"
                  :placeholder="$t('authorizeAttendances.filterBySpecialtyPlaceholder')"
                  label="label"
                  track-by="id"
                  :multiple="false"
                  :allow-empty="true"
                  select-label="Pressione ENTER para selecionar"
                  deselect-label="Pressione ENTER para remover"
                  selected-label="Pressione ENTER para selecionar"
                >
                  <template #option="props">
                    {{ props.option.id }} - {{ props.option.name }}
                  </template>
                  <template #singleLabel="{ option }">
                    <span v-if="option">{{ option.id }} - {{ option.name }}</span>
                  </template>
                </multiselect>
              </div>
              <div class="col-md-6">
                <label for="selectProfessional" class="form-label">{{ $t('authorizeAttendances.filterByProfessional') }}</label>
                <multiselect
                  id="selectProfessional"
                  :key="'professional-' + multiselectKey"
                  v-model="filters.professional"
                  :options="listProfessionalsOptions"
                  :placeholder="$t('authorizeAttendances.filterByProfessionalPlaceholder')"
                  label="label"
                  track-by="id"
                  :multiple="false"
                  :allow-empty="true"
                  select-label="Pressione ENTER para selecionar"
                  deselect-label="Pressione ENTER para remover"
                  selected-label="Pressione ENTER para selecionar"
                >
                  <template #option="props">
                    <span :class="{ 'professional-inactive': !isProfessionalActive(props.option) }">
                      {{ props.option.label || professionalFilterLabel(props.option) }}
                    </span>
                  </template>
                  <template #singleLabel="{ option }">
                    <span
                      v-if="option"
                      :class="{ 'professional-inactive': !isProfessionalActive(option) }"
                    >
                      {{ option.label || professionalFilterLabel(option) }}
                    </span>
                  </template>
                </multiselect>
                <small v-if="filters.patient && listProfessionalsOptions.length === 0" class="text-muted">
                  {{ $t('authorizeAttendances.noProfessionalsForPatient') }}
                </small>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-6">
                <label for="selectStatus" class="form-label">{{ $t('authorizeAttendances.filterByStatus') }}</label>
                <multiselect
                  id="selectStatus"
                  v-model="filters.statuses"
                  :options="statusOptions"
                  :placeholder="$t('authorizeAttendances.filterByStatusPlaceholder')"
                  label="label"
                  track-by="value"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :allow-empty="true"
                  select-label="Pressione ENTER para selecionar"
                  deselect-label="Pressione ENTER para remover"
                  selected-label="Pressione ENTER para selecionar"
                >
                  <template #option="props">
                    {{ props.option.label }}
                  </template>
                  <template #tag="{ option, remove }">
                    <span class="multiselect__tag">
                      <span>{{ option.label }}</span>
                      <i class="multiselect__tag-icon" @click.prevent="remove(option)"></i>
                    </span>
                  </template>
                </multiselect>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-3 d-flex align-items-end gap-2">
                <button type="button" class="btn btn-primary mr-2" @click="loadRecurrences" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  {{ $t('authorizeAttendances.search') }}
                </button>
                <button type="button" class="btn btn-outline-secondary" @click="clearFilters">
                  {{ $t('authorizeAttendances.clearFilters') }}
                </button>
              </div>
            </div>

            <div v-if="loadError" class="alert alert-danger" role="alert">
              <template v-if="loadErrorType === 'timeout'">
                <strong>{{ $t('authorizeAttendances.timeoutTitle') }}</strong><br>
                {{ $t('authorizeAttendances.timeoutMessage') }}
              </template>
              <template v-else>
                {{ $t('authorizeAttendances.loadError') }}
              </template>
            </div>

            <div v-if="!loading && !loadError && list.length === 0" class="alert alert-info" role="alert">
              {{ $t('authorizeAttendances.noResults') }}
            </div>

            <template v-else-if="!loading && !loadError">
              <div v-if="selectedKeys.length > 0" class="mb-2 d-flex align-items-center">
                <button
                  type="button"
                  class="btn btn-primary"
                  :disabled="authorizingBulk || cancelingBulk"
                  @click="authorizeSelected"
                >
                  <span v-if="authorizingBulk" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  {{ $t('authorizeAttendances.authorizeSelected') }} ({{ selectedKeys.length }})
                </button>
                <button
                  type="button"
                  class="btn btn-danger ms-2"
                  :disabled="authorizingBulk || cancelingBulk || selectedRows.length === 0"
                  @click="cancelSelected"
                >
                  <span v-if="cancelingBulk" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  {{ $t('authorizeAttendances.cancelOrDenySelected') }} ({{ selectedKeys.length }})
                </button>
              </div>
              <div class="row mb-3">
                <div class="col-sm-12 col-md-6">
                  <label class="form-label d-inline-flex align-items-center">
                    {{ $t('authorizeAttendances.searchInTable') }}
                    <b-form-input
                      v-model="searchQuery"
                      type="search"
                      :placeholder="$t('authorizeAttendances.searchPlaceholder')"
                      class="form-control form-control-sm ml-2 d-inline-block"
                      style="max-width: 280px;"
                    />
                  </label>
                </div>
                <div class="col-sm-12 col-md-6 text-md-right">
                  <label class="d-inline-flex align-items-center">
                    {{ $t('authorizeAttendances.perPage') }}
                    <b-form-select v-model="perPage" size="sm" :options="pageOptions" class="ml-2 d-inline-block" style="width: auto;" />
                  </label>
                </div>
              </div>
              <div class="table-responsive">
                <b-table
                  ref="authorizeTable"
                  :items="list"
                  :fields="tableFields"
                  :filter="searchQuery"
                  :filter-included-fields="searchFilterFields"
                  :per-page="perPage"
                  :current-page.sync="currentPage"
                  :busy="authorizingBulk"
                  responsive="sm"
                  hover
                  bordered
                  show-empty
                  :empty-text="$t('authorizeAttendances.noResults')"
                  :tbody-tr-class="rowClass"
                  :tbody-tr-attr="rowAttr"
                  @filtered="onFiltered"
                >
                  <template #head(select)>
                    <b-form-checkbox
                      :indeterminate.prop="selectAllIndeterminate"
                      :checked="selectAllChecked"
                      :disabled="nonAuthorizedItems.length === 0 || authorizingBulk"
                      aria-label="Selecionar todos"
                      @change="toggleSelectAll"
                    />
                  </template>
                  <template #cell(select)="{ item }">
                    <b-form-checkbox
                      :checked="isSelected(item)"
                      :disabled="authorizingBulk || cancelingBulk"
                      aria-label="Selecionar"
                      @change="toggleSelect(item)"
                    />
                  </template>
                  <template #cell(agenda_id)="{ item }">
                    {{ item.agenda_id || '–' }}
                  </template>
                  <template #cell(request_type)="{ item }">
                    <span v-if="item.request_type === 'reposicao'" class="badge bg-info">{{ $t('authorizeAttendances.requestTypeReplacement') }}</span>
                    <span v-else class="badge bg-secondary">{{ $t('authorizeAttendances.requestTypeAttendance') }}</span>
                  </template>
                  <template #cell(pacient)="{ item }">
                    {{ item.pacient || '–' }}
                  </template>
                  <template #cell(professional)="{ item }">
                    {{ professionalName(item) }}
                  </template>
                  <template #cell(time)="{ item }">
                    {{ item.start_time }} – {{ item.end_time }}
                  </template>
                  <template #cell(agreement)="{ item }">
                    {{ item.agreement || '–' }}
                  </template>
                  <template #cell(status)="{ item }">
                    <span v-if="!item.is_authorized" class="badge bg-danger text-white">{{ $t('authorizeAttendances.notAuthorized') }}</span>
                    <span v-else-if="item.is_authorized && item.professional_aware && (item.desc != null ? String(item.desc).trim().length : 0) > 2" class="badge bg-primary text-white">{{ $t('authorizeAttendances.attended') }}</span>
                    <span v-else-if="item.is_authorized && item.professional_aware" class="badge bg-info text-white">{{ $t('authorizeAttendances.aware') }}</span>
                    <span v-else class="badge bg-success text-white">{{ $t('authorizeAttendances.authorized') }}</span>
                  </template>
                </b-table>
              </div>
              <div class="d-flex justify-content-between align-items-center mt-2 flex-wrap">
                <span class="text-muted">{{ totalRows }} {{ $t('authorizeAttendances.records') }}</span>
                <b-pagination
                  v-if="totalRows > perPage"
                  v-model="currentPage"
                  :total-rows="totalRows"
                  :per-page="perPage"
                  size="sm"
                  class="mb-0"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import Multiselect from 'vue-multiselect'
import { http } from '@/helpers/easyindustriaapi/config'
import { validateAuthorizeAttendancePeriod } from '@/helpers/authorize-attendance-period'
import { getBlockedAttendanceKey } from '@/helpers/blocked-attendance'
import {
  isProfessionalActive,
  professionalFilterLabel,
  mapProfessionalOption,
} from '@/helpers/professionalStatus'
import Swal from 'sweetalert2'

export default {
  name: 'AuthorizeAttendances',
  components: { Layout, PageHeader, Multiselect },
  data() {
    const today = new Date().toISOString().slice(0, 10)
    return {
      breadcrumbItems: [
        { text: this.$t('menuitems.processes.text'), href: '#' },
        { text: this.$t('menuitems.processes.list.authorize-attendances'), active: true }
      ],
      list: [],
      listPatientsOptions: [],
      listProfessionalsOptions: [],
      listSpecialtiesOptions: [],
      multiselectKey: 0,
      loading: false,
      loadError: false,
      loadErrorType: null,
      authorizingBulk: false,
      cancelingBulk: false,
      selectedKeys: [],
      filters: {
        start_date: today,
        end_date: today,
        patient: null,
        professional: null,
        specialty: null,
        request_type: '',
        statuses: []
      },
      searchQuery: '',
      perPage: 25,
      currentPage: 1,
      totalRows: 0,
      pageOptions: [10, 25, 50, 100],
      searchFilterFields: ['pacient', 'professional', 'agreement', 'agenda_id', 'request_type'],
      highlightRecurrenceId: null
    }
  },
  computed: {
    statusOptions() {
      return [
        { value: 'not_authorized', label: this.$t('authorizeAttendances.notAuthorized') },
        { value: 'authorized', label: this.$t('authorizeAttendances.authorized') },
        { value: 'aware', label: this.$t('authorizeAttendances.aware') },
        { value: 'attended', label: this.$t('authorizeAttendances.attended') }
      ]
    },
    requestTypeOptions() {
      return [
        { value: '', text: this.$t('authorizeAttendances.filterAllTypes') },
        { value: 'atendimento', text: this.$t('authorizeAttendances.requestTypeAttendance') },
        { value: 'reposicao', text: this.$t('authorizeAttendances.requestTypeReplacement') }
      ]
    },
    tableFields() {
      return [
        { key: 'select', label: '', thStyle: { width: '2.5rem' }, tdClass: 'text-center align-middle', thClass: 'text-center align-middle' },
        { key: 'agenda_id', label: 'ID', sortable: true, thClass: 'text-center' },
        { key: 'request_type', label: this.$t('authorizeAttendances.requestType'), sortable: false },
        { key: 'pacient', label: this.$t('authorizeAttendances.patient'), sortable: true },
        { key: 'professional', label: this.$t('authorizeAttendances.professional'), sortable: true },
        { key: 'time', label: this.$t('authorizeAttendances.time'), sortable: false },
        { key: 'agreement', label: this.$t('authorizeAttendances.agreement'), sortable: true },
        { key: 'status', label: this.$t('authorizeAttendances.status'), sortable: false }
      ]
    },
    currentEmpresa() {
      const e = JSON.parse(localStorage.getItem('currentEmpresa'))
      return e || { id: null }
    },
    nonAuthorizedItems() {
      return this.list.filter((i) => !i.is_authorized)
    },
    selectedItems() {
      return this.list.filter((i) => !i.is_authorized && this.isSelected(i))
    },
    selectedRows() {
      return this.list.filter((i) => this.isSelected(i))
    },
    selectAllChecked() {
      return this.nonAuthorizedItems.length > 0 && this.selectedItems.length === this.nonAuthorizedItems.length
    },
    selectAllIndeterminate() {
      return this.selectedItems.length > 0 && this.selectedItems.length < this.nonAuthorizedItems.length
    }
  },
  created() {
    this.currentUser = JSON.parse(localStorage.getItem('user') || '{}')
    this.applyRouteQueryToFilters()
    this.loadSavedFilters()
    this.ensurePeriodFilters()
    this.loadPatients()
    this.loadProfessionals()
    this.loadSpecialties()
    this.loadRecurrences()
  },
  watch: {
    'filters.patient': {
      handler(newVal, oldVal) {
        if (newVal?.id === oldVal?.id) {
          return
        }

        this.loadProfessionals(newVal?.id || null)
      }
    },
    '$route.query': {
      handler() {
        this.applyRouteQueryToFilters()
        if (this.currentEmpresa?.id) {
          this.loadRecurrences()
        }
      },
      deep: true
    }
  },
  methods: {
    isProfessionalActive,
    professionalFilterLabel,
    filtersStorageKey() {
      const userId = this.currentUser?.id || 'na'
      const empresaId = this.currentEmpresa?.id || 'na'
      return `easysaude:authorize_attendances:filters:v1:u${userId}:e${empresaId}`
    },
    ensurePeriodFilters() {
      const today = new Date().toISOString().slice(0, 10)
      if (!this.filters.start_date) this.filters.start_date = today
      if (!this.filters.end_date) this.filters.end_date = today
    },
    loadSavedFilters() {
      try {
        const raw = localStorage.getItem(this.filtersStorageKey())
        if (!raw) return

        const parsed = JSON.parse(raw)
        const savedStatuses = Array.isArray(parsed?.statuses) ? parsed.statuses : []
        const allowed = new Set(this.statusOptions.map((option) => option.value))
        this.filters.statuses = this.statusOptions.filter((option) => savedStatuses.includes(option.value) && allowed.has(option.value))
      } catch (_) {
        // noop
      }
    },
    saveFilters() {
      try {
        const payload = {
          statuses: (this.filters.statuses || []).map((status) => status.value)
        }
        localStorage.setItem(this.filtersStorageKey(), JSON.stringify(payload))
      } catch (_) {
        // noop
      }
    },
    async loadPatients() {
      if (!this.currentEmpresa?.id) return
      try {
        const res = await http.get(`/atendimento/tabelas?empresa_id=${this.currentEmpresa.id}`)
        const patients = res.data?.pacients || []
        this.listPatientsOptions = patients.map((p) => {
          const id = p.id
          const name = p.nome || ''
          return {
            id,
            name,
            label: `${String(id)} - ${name}`
          }
        })
      } catch {
        this.listPatientsOptions = []
      }
    },
    async loadProfessionals(patientId = null) {
      if (!this.currentEmpresa?.id) return
      try {
        const params = new URLSearchParams({ empresa_id: this.currentEmpresa.id })
        if (patientId) {
          params.set('pacient_id', patientId)
        }

        const res = await http.get(`/atendimento/tabelas?${params.toString()}`)
        const professionals = res.data?.professionals || []
        this.listProfessionalsOptions = professionals.map((p) => mapProfessionalOption(p))

        if (this.filters.professional?.id) {
          const stillAvailable = this.listProfessionalsOptions.some((option) => option.id === this.filters.professional.id)
          if (!stillAvailable) {
            this.filters.professional = null
          }
        }
      } catch {
        this.listProfessionalsOptions = []
      }
    },
    async loadSpecialties() {
      if (!this.currentEmpresa?.id) return
      try {
        const res = await http.get(`/specialty/pluck?empresa_id=${this.currentEmpresa.id}`)
        const specialties = Array.isArray(res.data) ? res.data : []
        this.listSpecialtiesOptions = specialties.map((s) => {
          const id = s.id
          const name = s.name || ''
          return {
            id,
            name,
            label: `${String(id)} - ${name}`
          }
        })
      } catch {
        this.listSpecialtiesOptions = []
      }
    },
    applyRouteQueryToFilters() {
      const q = this.$route?.query || {}
      if (q.start_date) this.filters.start_date = q.start_date
      if (q.end_date) this.filters.end_date = q.end_date
      if (q.date) {
        this.filters.start_date = q.date
        this.filters.end_date = q.date
      }
      if (q.request_type) this.filters.request_type = q.request_type
      const recId = q.recurrence_id ? parseInt(q.recurrence_id, 10) : null
      this.highlightRecurrenceId = Number.isNaN(recId) ? null : recId
    },
    rowClass(item) {
      if (this.highlightRecurrenceId && item.id === this.highlightRecurrenceId) {
        return 'table-warning'
      }
      return null
    },
    rowAttr(item) {
      const id = item.id != null ? item.id : (item.replacement_request_id ? `repl-${item.replacement_request_id}` : null)
      return id != null ? { 'data-recurrence-id': String(id) } : {}
    },
    getAuthorizationFailureMessage(error) {
      const data = error?.response?.data
      if (data?.message) return data.message
      if (data?.error) return data.error
      if (data?.errors) {
        const messages = Object.values(data.errors).flat().filter(Boolean)
        if (messages.length > 0) return messages.join(' ')
      }
      return error?.message || this.$t('authorizeAttendances.authorizeSelectedError')
    },
    getMonthStart(dateValue) {
      const date = new Date(dateValue)
      if (Number.isNaN(date.getTime())) return null
      return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)).toISOString().slice(0, 10)
    },
    getMonthEnd(dateValue) {
      const date = new Date(dateValue)
      if (Number.isNaN(date.getTime())) return null
      return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).toISOString().slice(0, 10)
    },
    scrollToHighlightedRow() {
      if (!this.highlightRecurrenceId) return
      this.$nextTick(() => {
        const tbl = this.$refs.authorizeTable
        if (!tbl || !tbl.$el) return
        const row = tbl.$el.querySelector(`tr[data-recurrence-id="${this.highlightRecurrenceId}"]`)
        if (row) row.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      })
    },
    getQueryParams() {
      const params = new URLSearchParams()
      params.set('empresa_id', this.currentEmpresa.id)
      if (this.filters.start_date) params.set('start_date', this.filters.start_date)
      if (this.filters.end_date) params.set('end_date', this.filters.end_date)
      if (this.filters.patient?.id) params.set('pacient_id', this.filters.patient.id)
      if (this.filters.professional?.id) params.set('professional_id', this.filters.professional.id)
      if (this.filters.specialty?.id) params.set('specialty_id', this.filters.specialty.id)
      if (this.filters.request_type) params.set('request_type', this.filters.request_type)
      ;(this.filters.statuses || []).forEach((status) => {
        if (status?.value) params.append('status[]', status.value)
      })
      return params.toString()
    },
    validatePeriodFilters() {
      this.ensurePeriodFilters()
      const validation = validateAuthorizeAttendancePeriod(this.filters.start_date, this.filters.end_date)
      if (validation.valid) {
        return true
      }

      Swal.fire({
        icon: 'warning',
        title: this.$t('authorizeAttendances.periodValidationTitle'),
        text: this.$t(`authorizeAttendances.${validation.errorKey}`, { maxDays: validation.maxDays || 90 })
      })

      return false
    },
    loadRecurrences() {
      this.ensurePeriodFilters()
      if (this.currentEmpresa.id) {
        this.saveFilters()
      }
      if (!this.validatePeriodFilters()) {
        return
      }
      if (!this.currentEmpresa.id) {
        this.loadError = true
        return
      }
      this.loading = true
      this.loadError = false
      this.loadErrorType = null
      const query = this.getQueryParams()
      http.get(`/atendimento/authorize-attendances?${query}`)
        .then((res) => {
          this.list = res.data.data || []
          this.totalRows = this.list.length
          this.currentPage = 1
          this.scrollToHighlightedRow()
        })
        .catch((err) => {
          this.loadError = true
          this.list = []
          this.totalRows = 0
          if (!err.response) {
            this.loadErrorType = 'timeout'
          } else if (err.response?.data?.error === 'timeout' || err.response?.status === 408) {
            this.loadErrorType = 'timeout'
          } else {
            this.loadErrorType = 'general'
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
    clearFilters() {
      const today = new Date().toISOString().slice(0, 10)
      this.filters.start_date = today
      this.filters.end_date = today
      this.filters.patient = null
      this.filters.professional = null
      this.filters.specialty = null
      this.filters.request_type = ''
      this.filters.statuses = []
      this.multiselectKey += 1
      try {
        localStorage.removeItem(this.filtersStorageKey())
      } catch (_) {
        // noop
      }
      this.loadRecurrences()
    },
    professionalName(item) {
      return item.professional || '–'
    },
    getVirtualKey(item) {
      if (item.replacement_request_id) {
        return `repl-${item.replacement_request_id}`
      }
      return `${item.agenda_id}-${item.start}-${item.start_time}`
    },
    itemKey(item) {
      if (item.replacement_request_id) {
        return `repl-${item.replacement_request_id}`
      }
      return item.id != null ? item.id : `v-${item.agenda_id}-${item.start}-${item.start_time}`
    },
    isSelected(item) {
      const key = item.id != null ? item.id : this.getVirtualKey(item)
      return this.selectedKeys.includes(key)
    },
    toggleSelect(item) {
      const key = item.id != null ? item.id : this.getVirtualKey(item)
      const idx = this.selectedKeys.indexOf(key)
      if (idx === -1) {
        this.selectedKeys = [...this.selectedKeys, key]
      } else {
        this.selectedKeys = this.selectedKeys.filter((k) => k !== key)
      }
    },
    toggleSelectAll() {
      if (this.selectAllChecked) {
        this.selectedKeys = []
      } else {
        this.selectedKeys = this.nonAuthorizedItems.map((i) => (i.id != null ? i.id : this.getVirtualKey(i)))
      }
    },
    async authorizeSelected() {
      const items = this.selectedItems
      if (items.length === 0) return

      this.authorizingBulk = true
      let success = 0
      let failed = 0
      const blocked = []
      const blockedKeys = new Set()
      const failureMessages = []
      const params = new URLSearchParams({ empresa_id: this.currentEmpresa.id }).toString()

      const addBlocked = (item, reason) => {
        const name = item.pacient || item.patient_name || item.pacient_name || `#${item.pacient_id || item.agenda_id || ''}`
        const key = getBlockedAttendanceKey(item)
        if (!blockedKeys.has(key)) {
          blockedKeys.add(key)
          blocked.push({ name, reason })
        }
      }
      const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }[char]))

      try {
        for (const item of items) {
          try {
            if (item.control_sessions) {
              const monthStart = this.getMonthStart(item.start)
              const monthEnd = this.getMonthEnd(item.start)
              const query = {
                empresa_id: this.currentEmpresa.id,
                start_date: monthStart,
                end_date: monthEnd,
                request_type: 'atendimento',
                status: ['not_authorized', 'authorized', 'aware', 'attended']
              }
              if (item.pacient_id) query.pacient_id = item.pacient_id
              if (item.professional_id) query.professional_id = item.professional_id
              if (item.specialty_id) query.specialty_id = item.specialty_id

              const check = await http.get('/atendimento/authorize-attendances', { params: query })
              const rows = Array.isArray(check.data?.data) ? check.data.data : []
              const sameAgendaMonth = rows.filter((row) => {
                if (row?.agenda_id !== item.agenda_id) return false
                if (!row?.start) return false
                return String(row.start).slice(0, 7) === String(item.start || '').slice(0, 7)
              })
              const limit = Number(item.session_quantity || 0)
              if (limit > 0 && sameAgendaMonth.length >= limit) {
                addBlocked(item, this.$t('authorizeAttendances.sessionLimitReason'))
                continue
              }
            }

            if (item.replacement_request_id) {
              await http.put(`/atendimento/replacement-request/${item.replacement_request_id}/authorize?${params}`)
              success++
              const idx = this.list.findIndex((i) => i.replacement_request_id === item.replacement_request_id)
              if (idx !== -1) this.list.splice(idx, 1)
              continue
            }

            if (item.virtual) {
              const res = await http.post('/atendimento/authorize-by-agenda', {
                empresa_id: this.currentEmpresa.id,
                agenda_id: item.agenda_id,
                date: item.start,
                start_time: item.start_time,
                end_time: item.end_time
              })
              success++
              const updated = res.data.data
              const idx = this.list.findIndex(
                (i) => i.virtual && !i.replacement_request_id && i.agenda_id === item.agenda_id && i.start === item.start && i.start_time === item.start_time
              )
              if (idx !== -1 && updated) {
                this.$set(this.list, idx, { ...updated, virtual: false })
              }
              continue
            }

            await http.put(`/atendimento/recurrence/${item.id}/authorize?${params}`)
            success++
            item.is_authorized = true
          } catch (error) {
            const data = error?.response?.data || {}
            if (data.error === 'session_limit_reached') {
              addBlocked(item, data.message || this.$t('authorizeAttendances.sessionLimitReason'))
            } else {
              failed++
              failureMessages.push(this.getAuthorizationFailureMessage(error))
            }
          }
        }
      } finally {
        this.authorizingBulk = false
        this.selectedKeys = []
      }

      const statusLines = []
      if (success > 0) {
        statusLines.push(`<strong>${this.$t('authorizeAttendances.authorizedCount', { count: success })}</strong>`)
      }
      if (blocked.length > 0) {
        const blockedItems = blocked.map((entry) => `<li><strong>${escapeHtml(entry.name)}</strong>${entry.reason ? ` — ${escapeHtml(entry.reason)}` : ''}</li>`).join('')
        statusLines.push(`<div class="mt-2"><strong>${this.$t('authorizeAttendances.sessionLimitListTitle')}</strong><ul class="text-left mb-0">${blockedItems}</ul></div>`)
      }
      if (failed > 0) {
        const details = [...new Set(failureMessages)].filter(Boolean).map((message) => `<li>${escapeHtml(message)}</li>`).join('')
        statusLines.push(`<div class="mt-2">${this.$t('authorizeAttendances.failedCount', { count: failed })}${details ? `<ul class="text-left mb-0">${details}</ul>` : ''}</div>`)
      }

      if (statusLines.length > 0) {
        Swal.fire({
          icon: blocked.length > 0 || failed > 0 ? 'warning' : 'success',
          title: this.$t('authorizeAttendances.bulkStatusTitle'),
          html: statusLines.join('')
        })
      }

      if (success > 0) {
        this.loadRecurrences()
      }
    },
    async cancelSelected() {
      const selectedAll = this.list.filter((i) => this.isSelected(i))
      if (selectedAll.length === 0) return

      const authorizedItems = selectedAll.filter((i) => i.is_authorized && i.id != null)
      const notAuthorizedItems = selectedAll.filter((i) => !i.is_authorized)

      this.cancelingBulk = true

      let unauthorizedSuccess = 0
      let unauthorizedFailed = 0
      let denyUpdated = 0
      let ignoredReplacement = 0

      try {
        if (authorizedItems.length > 0) {
          const promises = authorizedItems.map((item) => {
            return http
              .put(`/atendimento/recurrence/${item.id}/unauthorize`, {
                empresa_id: this.currentEmpresa.id
              })
              .then(() => { unauthorizedSuccess++ })
              .catch(() => { unauthorizedFailed++ })
          })
          await Promise.all(promises)
        }

        if (notAuthorizedItems.length > 0) {
          const ids = []
          notAuthorizedItems.forEach((item) => {
            if (item.replacement_request_id) {
              ignoredReplacement++
              return
            }
            if (item.id != null) {
              ids.push(item.id)
            } else {
              ids.push(this.getVirtualKey(item))
            }
          })

          if (ids.length > 0) {
            const promptResult = await this.askDenyReason()
            if (promptResult) {
              const { tipo_falta, motivo } = promptResult
              const response = await http.post('/atendimento/falta/lote', {
                empresa_id: this.currentEmpresa.id,
                ids,
                tipo_falta,
                motivo
              })
              denyUpdated = response?.data?.updated || 0
            }
          }
        }

        this.selectedKeys = []

        if (unauthorizedFailed > 0) {
          Swal.fire({
            icon: 'warning',
            title: this.$t('authorizeAttendances.rollbackPartialTitle'),
            text: this.$t('authorizeAttendances.rollbackPartialMessage', {
              success: unauthorizedSuccess,
              failed: unauthorizedFailed
            })
          })
        } else if (unauthorizedSuccess > 0 || denyUpdated > 0) {
          Swal.fire({
            icon: 'success',
            title: this.$t('authorizeAttendances.cancelOrDenySuccess', {
              rollback: unauthorizedSuccess,
              denied: denyUpdated
            }),
            text: ignoredReplacement > 0
              ? this.$t('authorizeAttendances.replacementIgnored', { count: ignoredReplacement })
              : undefined
          })
        }

        this.loadRecurrences()
      } catch (e) {
        const msg = e?.response?.data?.error || e?.response?.data?.message || this.$t('authorizeAttendances.cancelOrDenyError')
        Swal.fire({
          icon: 'error',
          title: this.$t('authorizeAttendances.cancelOrDenyErrorTitle'),
          text: msg
        })
      } finally {
        this.cancelingBulk = false
      }
    },
    async askDenyReason() {
      const { value } = await Swal.fire({
        title: this.$t('authorizeAttendances.denyPromptTitle'),
        html: `
          <div class="text-start">
            <label for="deny-type" class="form-label mb-1">${this.$t('authorizeAttendances.denyTypeLabel')}</label>
            <select id="deny-type" class="swal2-input" style="width:100%">
              <option value="profissional">${this.$t('authorizeAttendances.denyTypeProfessional')}</option>
              <option value="paciente">${this.$t('authorizeAttendances.denyTypePatient')}</option>
            </select>
            <label for="deny-reason" class="form-label mt-2 mb-1">${this.$t('authorizeAttendances.denyReasonLabel')}</label>
            <textarea id="deny-reason" class="swal2-textarea" placeholder="${this.$t('authorizeAttendances.denyReasonPlaceholder')}"></textarea>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: this.$t('authorizeAttendances.denyConfirm'),
        cancelButtonText: this.$t('authorizeAttendances.denyCancel'),
        preConfirm: () => {
          const tipo_falta = document.getElementById('deny-type')?.value || 'profissional'
          const motivo = (document.getElementById('deny-reason')?.value || '').trim()
          if (!motivo) {
            Swal.showValidationMessage(this.$t('authorizeAttendances.denyReasonRequired'))
            return false
          }
          return { tipo_falta, motivo }
        }
      })

      return value || null
    }
  }
}
</script>
